/**
 * Authentication module entry point
 *
 * Provides environment-aware authentication provider selection.
 * Automatically chooses between MockAuthProvider (local) and OidcAuthProvider (dev/prod)
 * based on environment configuration.
 *
 * Provider-agnostic: Works with Cognito, Entra, or any OIDC provider.
 */

import React from 'react';
import type { EnvironmentConfig } from '../config/types';
import type { AuthConfig } from '../config/auth';
import { MockAuthProvider } from './MockAuthProvider';
import { OidcAuthProvider } from './AuthProvider';
import { toOidcConfig } from '../config/cognito';

// Re-export authentication hooks
export { useAuth, useUser, useHasRole, useAccessToken, useCanonicalClaims, useProviderLogout } from './hooks';
export type { AuthHook, UserProfile, ProviderLogoutHook } from './hooks';

// Re-export canonical claims types
export type { CanonicalUserClaims, ClaimMappingConfig } from './claim-mapping';
export { mapClaims, mapClaimsForProvider, hasGroup, isAdmin, getDisplayName } from './claim-mapping';

// Re-export provider components for direct usage if needed
export { MockAuthProvider } from './MockAuthProvider';
export { OidcAuthProvider } from './AuthProvider';

// Re-export provider utilities
export { getAuthProvider, useMockAuth, buildOidcConfig, buildLogoutArgs } from './providers/oidc';
export type { AuthProviderType, OidcProviderConfig, SignoutRedirectArgs } from './providers/types';

// Re-export the Edna role vocabulary and gating primitive.
//
// NOTE: `isAdmin` re-exported above from './claim-mapping' is UNRELATED — it
// reads Cognito group claims, not Edna roles. Do not use it for authorization
// gating; use `atLeast` with a `GATES` value.
export { ROLE_ORDER, ROLE_LABELS, LEAST_PRIVILEGED, GATES, parseRole, atLeast } from './roles';
export type { EdnaRole, Gate } from './roles';
export { useCallerRole } from './useCallerRole';
export type { CallerRole, CallerRoleStatus } from './useCallerRole';

interface AuthProviderProps {
  children: React.ReactNode;
  /** Legacy EnvironmentConfig or new AuthConfig */
  config: EnvironmentConfig | AuthConfig;
}

/**
 * Environment-aware authentication provider
 *
 * Automatically selects the appropriate authentication provider based on
 * the environment configuration:
 * - Mock authentication for local development (config.isMock === true)
 * - OIDC authentication for dev/prod (config.isMock === false)
 *
 * Supports both legacy EnvironmentConfig and new AuthConfig formats.
 *
 * Usage:
 *   import { AuthProvider } from '@/auth';
 *   import { getConfig } from '@/config';
 *
 *   const config = getConfig();
 *   <AuthProvider config={config}>
 *     <App />
 *   </AuthProvider>
 *
 * @param props.children - Child components
 * @param props.config - Environment configuration (legacy or provider-agnostic)
 * @returns Authentication provider component
 */
export function AuthProvider({ children, config }: AuthProviderProps): JSX.Element {
  // Detect config type and extract mock flag
  const isMockAuth = 'isMock' in config ? config.isMock : config.isMockAuth;

  // Use mock authentication for local development
  // MockAuthProvider provides the same react-oidc-context interface
  if (isMockAuth) {
    const mockEmail = import.meta.env.VITE_MOCK_USER_EMAIL as string | undefined;
    const mockName = import.meta.env.VITE_MOCK_USER_NAME as string | undefined;

    return React.createElement(
      MockAuthProvider,
      { mockEmail, mockName, children }
    );
  }

  // Determine OIDC config based on config type
  let oidcConfig;
  if ('isMock' in config) {
    // Legacy EnvironmentConfig - use toOidcConfig
    oidcConfig = toOidcConfig(config as EnvironmentConfig);
  } else {
    // New AuthConfig - use oidcConfig directly
    const authConfig = config as AuthConfig;
    if (!authConfig.oidcConfig) {
      throw new Error('OIDC configuration required for non-mock authentication');
    }
    oidcConfig = authConfig.oidcConfig;
  }

  // Use OIDC authentication for dev/prod
  return React.createElement(OidcAuthProvider, { config: oidcConfig, children });
}
